<template>
  <div class="input-fiat-amount-root">
    <field
      v-slot="{ field, errors }"
      :name="name"
      :rules="rules"
      :model-value="formattedValue"
      tag="div"
      validate-on-input
      @update:modelValue="onInput"
    >
      <div
        class="coin-amount-input"
        :class="{
          coinAmountInput__error: !!errors[0],
        }"
      >
        <div class="coin-amount-input__title">
          {{ $t(title) }}
          <slot v-if="limitIcon" name="popoverWithLimits" />
        </div>

        <div class="coin-amount-input__count">
          <!-- placeholder for showing balance in fiat or Max button -->
        </div>

        <div class="coin-amount-input__validator">
          <div class="coin-amount-input__validator__input">
            <input
              v-bind="field"
              ref="input"
              v-only-numbers.decimal
              class="validator__input"
              placeholder="0"
              autocomplete="off"
              @focus="onFocus"
            />
            <div
              class="error-message"
              @click="getValueFromError(errors[0])"
              v-html="errors[0]"
            />
          </div>
        </div>

        <div class="select-fiat-block">
          <select-fiat
            class="select-fiat-block__custom"
            :model-value="fiatId"
            :options="availableFiats"
            placeholder=""
            @update:modelValue="handleSelectFiat"
          />
        </div>
      </div>
    </field>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Field } from 'vee-validate'
import { formatNumber } from '@/shared/lib/formatNumber'
import { SelectFiat } from '@/features/SelectFiat'

export default {
  components: {
    Field,
    SelectFiat,
  },

  props: {
    // Unique name for the form allows VeeValidate distinguish different inputs
    name: {
      type: String,
    },
    // Current input value (from v-model)
    modelValue: {
      type: String,
    },
    // Kind of a label inside the input component
    title: {
      type: String,
      default: '',
    },
    // Current selected fiat id
    fiatId: {
      type: String,
    },
    // Array with objects that describe all available fiats
    availableFiats: {
      type: Array,
      default: () => [],
    },
    // String with validation rules for Veevalidate
    rules: {
      type: String,
      default: null,
    },
    // Min limit for the number in input (in the selected fiat)
    limitMin: {
      type: Number,
      default: 0,
    },
    // Max limit for the number in input (in the selected fiat)
    limitMax: {
      type: Number,
      default: Infinity,
    },
    // Should we focus on the input when the component is mounted
    focusWhenMounted: {
      type: Boolean,
      default: false,
    },
    // Flag - should we show an icon with the popover about limits
    limitIcon: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'focus', 'update:fiat'],

  computed: {
    ...mapState('coinService', ['coins']),
    ...mapGetters('coinService', ['transformeredBalances']),

    formattedValue() {
      return String(this.modelValue)
    },
  },

  methods: {
    formatNumber,

    // When user focus on the input to fulfill amount
    onFocus() {
      this.$emit('focus')
    },

    // When user input a new sum in the field
    onInput(inputValue) {
      if (inputValue === '0.') {
        this.$emit('update:modelValue', '')
        return
      }

      // If the input value is ".", change it to "0."
      if (inputValue === '.' || inputValue === '0') {
        this.$emit('update:modelValue', '0.')
        return
      }

      this.$emit('update:modelValue', inputValue)
    },

    // When user selected another fiat
    handleSelectFiat(value) {
      this.$emit('update:fiat', value)
    },

    getValueFromError(error) {
      const match = error.match(
        /<span class='error-message-sum'>([\d,.]+).*<\/span>/
      )
      if (match) {
        const amount = +match[1]
        this.$emit('update:modelValue', `${amount}`)
      }
    },
  },

  mounted() {
    if (this.focusWhenMounted) {
      // TODO: Maybe to focus on the CoinDropdown when mounted?
    }
  },

  watch: {
    // Workaround - to force validation if value changed from outside the component
    async value() {
      await this.$refs.validator.validate()
    },
  },
}
</script>

<style scoped lang="scss">
.select-fiat-block {
  display: flex;
  justify-content: end;

  :deep(.select-fiat-block__custom) {
    .select-fiat {
      position: static;

      .base-select--activator {
        background-color: var(--bg-35);
        height: 36px;
        gap: 0;
        padding: 6px 10px;
        min-width: 110px;

        .coin-dropdown__main {
          min-width: 40px;
          margin-left: 5px;
        }
      }

      .base-select--activator:hover {
        background-color: var(--bg-4);
      }

      .base-select--main {
        left: 5%;
        right: 21px;
        top: 100px;
        z-index: 70;
        max-height: 310px;

        ul {
          max-height: calc(310px - 60px);
        }
      }
    }
  }
}

.coin-amount-input {
  height: 120px;
  background-color: var(--bg-1);
  border: 1px solid var(--bg-35);
  border-radius: 12px;
  padding: 20px;
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;

  &__title {
    display: flex;
    gap: 5px;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-3);
  }

  &__count {
    display: flex;
    justify-content: end;
    align-self: center;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-3);

    &__btn {
      color: var(--brand);
      font-size: 14px;
      font-weight: 500;
      margin-left: 10px;
    }

    &__btn:hover {
      color: var(--brand-hover);
    }
  }

  &__validator {
    height: 48px;
    &__input {
      input {
        outline: none;
        background-color: var(--bg-1);
        font-size: 20px;
        font-weight: 700;
        display: block;
        width: 100%;
        color: var(--text-1);
        min-height: 26px;
      }

      input:hover {
        cursor: pointer;
      }

      input::placeholder {
        color: var(--text-4);
        font-size: 20px;
        font-weight: 700;
      }
    }
  }
}

.coin-amount-input:focus-within {
  border: 1px solid var(--brand);
}

.coin-amount-input.coinAmountInput__error {
  border: 1px solid var(--error);
}

@media (max-width: 768px) {
  .select-fiat-block {
    :deep(.select-fiat-block__custom) {
      .select-fiat {
        .base-select--activator {
          height: 36px;
          padding-left: 8px;
          padding-right: 12px;

          .coin-dropdown__main-icon {
            width: 20px;
          }
        }
        .base-select--main {
          width: auto;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .select-fiat-block {
    :deep(.select-fiat-block__custom) {
      .select-fiat {
        .base-select--main {
          width: auto;
          left: 5%;
        }
      }
    }
  }
}

.error-message {
  font-size: 12px;
  font-weight: 500;
  color: var(--error);
}
</style>

<style>
.error-message-sum {
  text-decoration-line: underline;
  cursor: pointer;
}
</style>
